import { useCallback, useMemo, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ClipboardList } from 'lucide-react';
import {
  EmptyState,
  HelpAnchor,
  SkeletonBlock,
  SkeletonCardGrid,
  SurfacePageBody,
  SurfacePageHeader,
  SurfacePageLayout,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { AskStrip } from '@/versions/v1/chrome-oem/AskStrip';
import { MINES, MINE_ORDER } from '@/versions/v1/data/core';
import { unpackTable } from '@/versions/v1/shared/columnar';
import { useOemData, type InventoryLine, type WorkOrder } from '@/versions/v1/shared/useOemData';
import { useLakeDoc } from '@/versions/v1/shared/lake-drawer';
import type { MineCode } from '@/versions/v1/schema/machine';
import { MeasureTile, SceneSection, SourceLink, TonePill } from '@/versions/v1/shared/oem-tiles';
import { useRowsDrawer } from '@/versions/v1/modules/service/source-slice';
import {
  TECH_JOB_STATUSES,
  inventorySlice,
  jobCardGroups,
  jobCardsSummary,
  workOrderSlice,
  type JobCardModel,
  type JobCardsCtx,
} from './JobCardsScene.transform';

// =========================================================================
// Job cards. Route `/job-cards`. Fork-owned (zone C), P5b lane.
//
// Port of the demo's `renderJobcards` (source line 2764).
//
// A job card is the work instruction a technician gets on the app: what is
// probably wrong (ranked from the fleet's own history, not from a manual),
// the controlled procedure, the tools for that system, the parts, and what it
// will cost. It is the clearest single artefact in the whole demo for the
// "the data is already there" argument — every one of those blocks is derived
// from data the customer already has, and none of it is typed in.
//
// The demo showed one card at a time behind a list of sixty-eight. Here every
// open job is a card, grouped by site, because the question this page answers
// is "what is the crew doing this week".
//
// The WO number, the actual cost and each part number are source links, as
// the demo's `FIG()` values were: they open the `oem-source` drawer on the
// ledger row or the stock lines behind them. The rows are already loaded, so
// the drawer is `useRowsDrawer` over them rather than a second fetch.
//
// UI layer.
// =========================================================================

function useJobCardsData(): {
  ctx: JobCardsCtx | null;
  inventory: readonly InventoryLine[];
  loading: boolean;
  error: Error | null;
} {
  const service = useOemData('service');
  const workOrders = useOemData('work-orders');
  const parts = useOemData('parts');
  const docs = useOemData('doc-index');

  const loading = service.loading || workOrders.loading || parts.loading || docs.loading;
  const error = service.error ?? workOrders.error ?? parts.error ?? docs.error ?? null;

  const ctx = useMemo<JobCardsCtx | null>(() => {
    if (!service.data || !workOrders.data || !parts.data || !docs.data) return null;
    return {
      techJobs: service.data.tapp_jobs,
      workOrders: unpackTable<WorkOrder>(workOrders.data.wos),
      parts: parts.data.parts,
      documents: docs.data.docs,
    };
  }, [service.data, workOrders.data, parts.data, docs.data]);

  return { ctx, inventory: parts.data?.inventory ?? [], loading, error };
}

export function JobCardsScene() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const openLakeDoc = useLakeDoc();
  const { ctx, inventory, loading, error } = useJobCardsData();
  const openRows = useRowsDrawer();

  const openWorkOrder = useCallback(
    (woId: string) => {
      if (!ctx) return;
      openRows('wos', workOrderSlice(ctx.workOrders, woId), {
        filter: `wo_id=${woId}`,
        total: ctx.workOrders.length,
      });
    },
    [ctx, openRows],
  );

  const openPart = useCallback(
    (partNo: string) =>
      openRows('inv', inventorySlice(inventory, partNo), {
        filter: `part_no=${partNo}`,
        total: inventory.length,
      }),
    [inventory, openRows],
  );

  const mineParam = params.get('site');
  const statusParam = params.get('status');
  const focusJob = params.get('job');

  const filters = useMemo(
    () => ({
      mine: MINE_ORDER.includes(mineParam as MineCode) ? (mineParam as MineCode) : null,
      status: (TECH_JOB_STATUSES as readonly string[]).includes(statusParam ?? '')
        ? statusParam
        : null,
      openOnly: params.get('open') !== '0' && statusParam !== 'Completed',
    }),
    [mineParam, statusParam, params],
  );

  const groups = useMemo(
    () => (ctx ? jobCardGroups(ctx, filters, MINE_ORDER) : []),
    [ctx, filters],
  );
  const summary = useMemo(
    () => (ctx ? jobCardsSummary(ctx, groups) : null),
    [ctx, groups],
  );

  const setParam = useCallback(
    (key: string, value: string | null) => {
      const next = new URLSearchParams(params);
      if (value === null || value === '') next.delete(key);
      else next.set(key, value);
      setParams(next);
    },
    [params, setParams],
  );

  return (
    <SurfacePageLayout width="full">
      <Commentable name="JobCardsScene">
        <SurfacePageBody className="@container max-w-[1400px] space-y-8 pt-6">
          <SurfacePageHeader
            kicker="Workflows"
            title="Job cards"
            description="Work instructions assembled per job: possible causes ranked from the fleet's own work-order history, the tools for that system, a quote from the rate card and the catalogue, and the steps from the controlled procedure — pushed to the technician's app and closed with the customer's sign-off."
            actions={<HelpAnchor doc="workflows/job-cards" />}
            meta={
              summary ? (
                <span className="mono text-[11.5px]" style={{ color: 'var(--ink-tertiary)' }}>
                  {`${summary.total} jobs · ${summary.open} open · ${summary.shown} shown`}
                </span>
              ) : null
            }
          />

          <AskStrip view="jobcards" />

          {error ? (
            <div
              role="alert"
              className="rounded-xl border p-4 text-sm"
              style={{
                borderColor: 'var(--hairline)',
                background: 'var(--crit-soft)',
                color: 'var(--crit)',
              }}
            >
              {`The job cards could not be loaded (${error.message}). A card with a missing cause
              ranking or a missing procedure would be worse than no card, so nothing is shown.`}
            </div>
          ) : null}

          {/* ------------------------------------------------------- */}
          {/* Filters                                                 */}
          {/* ------------------------------------------------------- */}
          <Commentable name="Filters">
            <div className="flex flex-wrap items-end gap-3">
              <label className="flex flex-col gap-1">
                <span
                  className="text-[10px] font-semibold uppercase tracking-[0.08em]"
                  style={{ color: 'var(--ink-tertiary)' }}
                >
                  Site
                </span>
                <select
                  value={filters.mine ?? ''}
                  onChange={(e) => setParam('site', e.target.value)}
                  className="h-8 rounded-lg border px-2 text-[12px]"
                  style={{
                    borderColor: 'var(--hairline)',
                    background: 'var(--surface-2)',
                    color: 'var(--ink-primary)',
                  }}
                >
                  <option value="">All sites</option>
                  {MINE_ORDER.map((m) => (
                    <option key={m} value={m}>
                      {MINES[m].name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1">
                <span
                  className="text-[10px] font-semibold uppercase tracking-[0.08em]"
                  style={{ color: 'var(--ink-tertiary)' }}
                >
                  Status
                </span>
                <select
                  value={filters.status ?? ''}
                  onChange={(e) => setParam('status', e.target.value)}
                  className="h-8 rounded-lg border px-2 text-[12px]"
                  style={{
                    borderColor: 'var(--hairline)',
                    background: 'var(--surface-2)',
                    color: 'var(--ink-primary)',
                  }}
                >
                  <option value="">Open jobs</option>
                  {TECH_JOB_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </Commentable>

          {/* ------------------------------------------------------- */}
          {/* Summary                                                 */}
          {/* ------------------------------------------------------- */}
          <Commentable name="Summary">
            {summary ? (
              <div className="grid grid-cols-2 gap-3 @2xl:grid-cols-4">
                <MeasureTile eyebrow="Cards shown" value={summary.shown} />
                <MeasureTile eyebrow="Open jobs" value={summary.open} tone="warn" />
                <MeasureTile eyebrow="Sites" value={summary.sites} />
                <MeasureTile eyebrow="Technicians" value={summary.technicians} />
              </div>
            ) : (
              <SkeletonCardGrid count={4} cols={2} />
            )}
          </Commentable>

          {/* ------------------------------------------------------- */}
          {/* Cards, grouped by site                                  */}
          {/* ------------------------------------------------------- */}
          <Commentable name="Cards">
            {!ctx ? (
              <SkeletonBlock className="h-96 rounded-xl" />
            ) : groups.length === 0 ? (
              <EmptyState
                icon={<ClipboardList className="h-8 w-8 text-zinc-400" aria-hidden="true" />}
                title="No job cards match"
                description="Change the site or the status to bring cards back."
              />
            ) : (
              <div className="space-y-8">
                {groups.map((group) => (
                  <SceneSection
                    key={group.mine}
                    title={group.siteName}
                    subtitle={`${group.cards.length} ${group.cards.length === 1 ? 'card' : 'cards'}`}
                  >
                    <div className="grid grid-cols-1 gap-3 @4xl:grid-cols-2">
                      {group.cards.map((card) => (
                        <JobCardPanel
                          key={card.job.job_id}
                          card={card}
                          focused={focusJob === card.job.job_id}
                          onMachine={(id) => navigate(`/machines/${encodeURIComponent(id)}`)}
                          onWorkOrder={openWorkOrder}
                          onPart={openPart}
                          onProcedure={(file, title) =>
                            openLakeDoc({
                              file,
                              connector: 'SharePoint · OEM library',
                              title,
                            })
                          }
                        />
                      ))}
                    </div>
                  </SceneSection>
                ))}
              </div>
            )}
          </Commentable>

          <p className="sr-only" role="status">
            {loading && !ctx
              ? 'Loading the job cards.'
              : `${summary?.shown ?? 0} job cards across ${summary?.sites ?? 0} sites.`}
          </p>
        </SurfacePageBody>
      </Commentable>
    </SurfacePageLayout>
  );
}

/** One job card. The blocks are the demo's, in the demo's order. */
function JobCardPanel({
  card,
  focused,
  onMachine,
  onWorkOrder,
  onPart,
  onProcedure,
}: {
  card: JobCardModel;
  focused: boolean;
  onMachine: (machineId: string) => void;
  /** Open the ledger row behind the WO number and the actual. */
  onWorkOrder: (woId: string) => void;
  /** Open the stock lines for one part number. */
  onPart: (partNo: string) => void;
  onProcedure: (file: string, title: string) => void;
}) {
  const ref = useRef<HTMLElement | null>(null);
  const { job } = card;

  // A card arrived at by `?job=` scrolls itself into view once. Deep links
  // into this page come from the board and the alert pages, and landing at
  // the top of a thirteen-card list is the same as not linking at all.
  useEffect(() => {
    if (focused && ref.current) {
      ref.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [focused]);

  return (
    <article
      ref={ref}
      className="flex min-w-0 flex-col gap-3 rounded-xl border p-4"
      style={{
        borderColor: focused ? 'var(--accent)' : 'var(--hairline)',
        background: 'var(--surface-2)',
      }}
    >
      <header className="min-w-0">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h3
            className="m-0 text-[13px] font-semibold leading-snug"
            style={{ color: 'var(--ink-primary)' }}
          >
            <span className="mono">{job.job_id}</span>
            {' · '}
            <SourceLink onClick={() => onMachine(job.machine)} className="mono !text-[13px]">
              {job.machine}
            </SourceLink>
            <span style={{ color: 'var(--ink-tertiary)' }}>{` (${card.model})`}</span>
          </h3>
          <TonePill tone={job.status === 'Completed' ? 'good' : job.status === 'In progress' ? 'warn' : 'flat'}>
            {job.status}
          </TonePill>
        </div>
        <p className="m-0 mt-1 text-[11.5px] leading-[1.55]" style={{ color: 'var(--ink-tertiary)' }}>
          {`${card.component || job.job_type} · ${job.customer} · WO `}
          {card.woId ? (
            <SourceLink onClick={() => onWorkOrder(card.woId as string)} className="mono">
              {job.wo_ref}
            </SourceLink>
          ) : (
            <span className="mono">{job.wo_ref}</span>
          )}
          {`${job.external_ref ? ` · Maximo ${job.external_ref}` : ''} · ${job.technician} · on site ${job.on_site.replace('T', ' ')}${job.off_site ? ` → ${job.off_site.replace('T', ' ')}` : ''}`}
        </p>
        {card.findings ? (
          <p className="m-0 mt-2 text-[12px] leading-[1.55]" style={{ color: 'var(--ink-secondary)' }}>
            {card.findings}
          </p>
        ) : null}
      </header>

      {/* Causes */}
      <section className="min-w-0">
        <Eyebrow>
          {card.causes.length
            ? `Possible causes · from ${card.causeBase} work orders on this component across the fleet`
            : 'Possible causes'}
        </Eyebrow>
        {card.causes.length === 0 ? (
          <p className="m-0 text-[11.5px]" style={{ color: 'var(--ink-tertiary)' }}>
            No corrective history on this component — preventive work.
          </p>
        ) : (
          <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
            {card.causes.map((cause) => (
              <li key={cause.cause} className="flex items-start gap-3">
                <span className="min-w-0 flex-1">
                  <span
                    className="block text-[12px] font-medium leading-snug"
                    style={{ color: 'var(--ink-primary)' }}
                  >
                    {cause.cause}
                  </span>
                  {cause.eg ? (
                    <span
                      className="block text-[10.5px] leading-[1.5]"
                      style={{ color: 'var(--ink-tertiary)' }}
                    >
                      {`e.g. ${cause.eg.slice(0, 90)}`}
                    </span>
                  ) : null}
                </span>
                <span className="flex flex-none items-center gap-2">
                  <span
                    aria-hidden="true"
                    className="hidden h-1.5 w-16 overflow-hidden rounded-full @xl:block"
                    style={{ background: 'var(--hairline)' }}
                  >
                    <span
                      className="block h-full rounded-full"
                      style={{ width: `${cause.pct}%`, background: 'var(--accent)' }}
                    />
                  </span>
                  <span
                    className="mono w-9 text-right text-[12px] font-semibold"
                    style={{ color: 'var(--ink-primary)' }}
                  >
                    {`${cause.pct}%`}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Procedure */}
      <section className="min-w-0">
        <Eyebrow>
          {card.procedure
            ? `Steps to follow · ${card.procedure.file.split('_')[0]}`
            : 'Steps to follow'}
        </Eyebrow>
        {card.procedure ? (
          <>
            <p
              className="m-0 max-h-24 overflow-hidden text-[11.5px] leading-[1.55]"
              style={{ color: 'var(--ink-secondary)' }}
            >
              {`${card.procedure.excerpt
                .replace(/-{3,}/g, ' ')
                .replace(/\s{2,}/g, ' ')
                .slice(0, 340)}…`}
            </p>
            <p className="m-0 mt-1.5">
              <SourceLink
                onClick={() =>
                  onProcedure(
                    (card.procedure as { file: string }).file,
                    (card.procedure as { title: string }).title,
                  )
                }
              >
                Open the controlled procedure ›
              </SourceLink>
            </p>
          </>
        ) : (
          <p className="m-0 text-[11.5px]" style={{ color: 'var(--ink-tertiary)' }}>
            No controlled procedure matches this component.
          </p>
        )}
      </section>

      {/* Tools */}
      <section className="min-w-0">
        <Eyebrow>Tools needed</Eyebrow>
        <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0">
          {card.tools.map((tool) => (
            <li
              key={tool}
              className="rounded-full border px-2.5 py-1 text-[11px]"
              style={{ borderColor: 'var(--hairline)', color: 'var(--ink-secondary)' }}
            >
              {tool}
            </li>
          ))}
        </ul>
      </section>

      {/* Parts */}
      <section className="min-w-0">
        <Eyebrow>Parts</Eyebrow>
        {card.parts.length === 0 ? (
          <p className="m-0 text-[11.5px]" style={{ color: 'var(--ink-tertiary)' }}>
            None.
          </p>
        ) : (
          <ul className="m-0 flex list-none flex-col gap-1 p-0">
            {card.parts.map((part) => (
              <li
                key={part.part_no}
                className="flex flex-wrap items-baseline justify-between gap-2 text-[11.5px]"
                style={{ color: 'var(--ink-secondary)' }}
              >
                <span className="min-w-0">
                  <SourceLink onClick={() => onPart(part.part_no)} className="mono">
                    {part.part_no}
                  </SourceLink>
                  {` ${part.description} × ${part.qty}`}
                </span>
                <span className="mono" style={{ color: 'var(--ink-primary)' }}>
                  {part.amount ?? '—'}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Labour lines */}
      <section className="min-w-0">
        <Eyebrow>Labour lines · technician app</Eyebrow>
        {job.labour_lines.length === 0 ? (
          <p className="m-0 text-[11.5px]" style={{ color: 'var(--ink-tertiary)' }}>
            Not started.
          </p>
        ) : (
          <table className="w-full border-collapse text-left text-[11.5px]">
            <caption className="sr-only">{`Labour booked on ${job.job_id} in the technician app`}</caption>
            <tbody>
              {job.labour_lines.map((line, i) => (
                <tr
                  key={`${line.activity}-${i}`}
                  style={{ borderTop: i ? '1px solid var(--hairline)' : undefined }}
                >
                  <td className="py-1 pr-3 align-top" style={{ color: 'var(--ink-secondary)' }}>
                    {line.activity}
                  </td>
                  <td
                    className="mono whitespace-nowrap py-1 text-right align-top"
                    style={{ color: 'var(--ink-secondary)' }}
                  >
                    {`${line.hours} h`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Quote */}
      <section className="min-w-0">
        <Eyebrow>{job.status === 'Completed' ? 'Quote and actual' : 'Quote · estimate'}</Eyebrow>
        <table className="w-full border-collapse text-left text-[11.5px]">
          <caption className="sr-only">{`Priced quote for ${job.job_id}`}</caption>
          <tbody>
            {card.quote.map((line, i) => {
              const total = i === card.quote.length - 1;
              return (
                <tr key={line.label} style={{ borderTop: '1px solid var(--hairline)' }}>
                  <td
                    className="py-1.5 pr-3 align-top"
                    style={{
                      color: total ? 'var(--ink-primary)' : 'var(--ink-secondary)',
                      fontWeight: total ? 600 : 400,
                    }}
                  >
                    {line.label}
                  </td>
                  <td
                    className="mono whitespace-nowrap py-1.5 text-right align-top"
                    style={{
                      color: total ? 'var(--ink-primary)' : 'var(--ink-secondary)',
                      fontWeight: total ? 600 : 400,
                    }}
                  >
                    {line.amount ?? 'not priced for this site'}
                  </td>
                </tr>
              );
            })}
            {card.actual ? (
              <tr style={{ borderTop: '1px solid var(--hairline)' }}>
                <td className="py-1.5 pr-3 align-top" style={{ color: 'var(--ink-secondary)' }}>
                  Actual (SAP PM / ledger)
                </td>
                <td
                  className="mono whitespace-nowrap py-1.5 text-right align-top"
                  style={{ color: 'var(--ink-secondary)' }}
                >
                  <SourceLink
                    onClick={() => onWorkOrder(card.woId as string)}
                    className="mono"
                  >
                    {card.actual}
                  </SourceLink>
                  {card.actualDowntime ? (
                    <span
                      className="block text-[10.5px]"
                      style={{ color: 'var(--ink-tertiary)' }}
                    >
                      {`+ ${card.actualDowntime} lost production`}
                    </span>
                  ) : null}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </section>

      {/* Checklist */}
      <section className="min-w-0">
        <Eyebrow>Checklist</Eyebrow>
        <ul className="m-0 flex list-none flex-col gap-1 p-0">
          {card.checklist.map((row) => (
            <li
              key={row.item}
              className="flex items-baseline justify-between gap-3 text-[11.5px]"
              style={{ color: 'var(--ink-secondary)' }}
            >
              <span>{row.item}</span>
              <TonePill tone={row.result === 'Pass' ? 'good' : 'flat'}>{row.result}</TonePill>
            </li>
          ))}
        </ul>
      </section>

      {job.customer_signoff ? (
        <p className="m-0 text-[11px]" style={{ color: 'var(--ink-tertiary)' }}>
          {`Signed off by ${job.customer_signoff.name} · ${job.customer_signoff.time.replace('T', ' ')} · in the technician app · CSS survey sent`}
        </p>
      ) : null}
    </article>
  );
}

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="mb-1.5 text-[10px] font-semibold uppercase leading-4 tracking-[0.08em]"
      style={{ color: 'var(--ink-tertiary)' }}
    >
      {children}
    </div>
  );
}

export default JobCardsScene;
